import { notification } from 'antd';

// A FNSKU is valid only if it is a real, non-empty value. 'N/A' (what resolveFnsku
// returns when nothing is found) is treated as missing — we never print a label with it.
export const isValidFnsku = (value) => {
  const v = value == null ? '' : String(value).trim();
  return !!v && v.toUpperCase() !== 'N/A';
};

// Single error toast for a missing FNSKU. No fallback to ASIN or anything else —
// label generation must abort when the FNSKU is null.
export const notifyMissingFnsku = (label) => {
  notification.error({
    message: 'FNSKU is null',
    description: label
      ? `Cannot generate label — FNSKU is missing for ${label}.`
      : 'Cannot generate label — one or more items have a null FNSKU.',
    top: 65,
  });
};

// Amazon label owner NONE = the item ships on its manufacturer barcode; Amazon neither requires
// nor accepts an FNSKU label for it, so we must not generate one. Any other value (SELLER /
// AMAZON / unknown-NULL) prints as usual.
export const isFnskuLabelSuppressed = (labelOwner) =>
  String(labelOwner || '').trim().toUpperCase() === 'NONE';

export const notifyFnskuLabelSuppressed = (label) => {
  notification.info({
    message: 'FNSKU label not required',
    description: label
      ? `${label} has Label Owner "NONE" — Amazon does not require an FNSKU label for this item.`
      : 'Label Owner is "NONE" — Amazon does not require an FNSKU label for these items.',
    top: 65,
  });
};

export const resolveFnsku = (...items) => {
  for (const item of items) {
    if (!item) continue;

    const directValue = [
      item.fnsku,
      item.fulfillmentNetworkSku,
      item.seller_sku?.fnsku,
      item.sellerSku?.fnsku,
      item.sellersku?.fnsku,
      item.vendoritem?.seller_sku?.fnsku,
      item.vendor_item?.seller_sku?.fnsku,
      item.sku_info?.fnsku,
      item.skuInfo?.fnsku,
      item.products_v2?.[0]?.fnsku
    ].find(value => value && String(value).trim());

    if (directValue) return String(directValue).trim();

    const identifier = (item.product_details || item.productDetails || []).find((detail) => {
      const type = detail?.identifier_type || detail?.type || detail?.name;
      return String(type || '').toLowerCase() === 'fnsku';
    });

    const identifierValue = identifier?.identifier_value || identifier?.value || identifier?.fnsku;
    if (identifierValue && String(identifierValue).trim()) return String(identifierValue).trim();
  }

  return 'N/A';
};

export const findPackingItemBySkuId = (packingItems = [], skuId) => (
  packingItems.find(item => String(item.sku_id ?? item.id) === String(skuId)) || {}
);

export const buildPackNowLabelItems = ({ boxItems = [], packingItems = [], fallbackItem = {} }) => (
  boxItems.map((boxItem) => {
    const matchedItem = findPackingItemBySkuId(packingItems, boxItem?.sku_id);
    const fnsku = resolveFnsku(boxItem, matchedItem, fallbackItem);
    const quantity = Number(boxItem?.boxed_quantity || boxItem?.units || boxItem?.quantity || 0);
    const expiration = boxItem?.expiration || matchedItem?.expiration || fallbackItem?.expiration;

    return {
      sku_id: boxItem?.sku_id,
      fnsku,
      fulfillmentNetworkSku: fnsku,
      quantity,
      units: quantity,
      expiryDate: expiration ? [expiration] : []
    };
  })
);

export const formatBoxCode = (boxCode, boxId) => {
  if (!boxCode) return boxId ? `BX${boxId}` : 'BX01';
  return boxCode;
};
